/**
 * The regular-width source list (F-NAV-07 … F-NAV-11).
 *
 * Two things here are easy to get wrong and are therefore explicit rather than
 * incidental. First, the Ringside row is an **action, not a destination**: it
 * never carries `aria-current`, because a screen-reader user who hears
 * "selected" on a row that starts a workout has been told the wrong thing about
 * what pressing it does. Second, the status block is a single accessibility
 * element — three visually separate lines that are one sentence when spoken —
 * so its visible text is hidden from AT and the sentence is supplied once.
 */

import type { ReactNode } from "react";
import { Text, VisuallyHidden } from "@ff/ui";
import type { ShellActivity } from "../app-state/activity";
import type { ShortcutBinding } from "../app-state/shortcuts";
import { useShortcutLabel } from "../app-state/shortcuts";
import type { DestinationId } from "./destinations";
import { WORDMARK_EYEBROW, WORDMARK_TITLE, destinationFor } from "./destinations";
import type { BuildTool } from "./sidebarModel";
import {
  RINGSIDE_ACTION_TITLE,
  SIDEBAR_GROUPS,
  isSidebarRowSelected,
  resumeCard,
  ringsideQuickAction,
  sidebarStatus,
} from "./sidebarModel";
import styles from "./Sidebar.module.css";

/** F-NAV-10, verbatim. */
export const NEW_ENTRY_LABEL = "New entry" as const;

/** The New-entry binding, remapped from ⌘N per §9.2 for the same reason as ⌘1–4. */
export const NEW_ENTRY_SHORTCUT: ShortcutBinding = { key: "n", ctrl: true, alt: true };

export const SIDEBAR_LABEL = "Sections" as const;

export interface SidebarProps {
  readonly selected: DestinationId;
  readonly buildTool: BuildTool;
  readonly activity: ShellActivity;
  readonly onSelect: (destination: DestinationId, tool?: BuildTool) => void;
  readonly onRingside: () => void;
}

export function Sidebar({
  selected,
  buildTool,
  activity,
  onSelect,
  onRingside,
}: SidebarProps): ReactNode {
  const label = useShortcutLabel();
  const quickAction = ringsideQuickAction(activity);
  const card = resumeCard(activity);
  const status = sidebarStatus(activity);
  const newEntry = activity.createJournalEntry;

  // Generated from the binding, never written as a literal: §9.2 names the
  // stale-hint failure directly, and the native sidebar's own string
  // ("⌘1–4 switches sections") is exactly the one the web remap invalidates.
  const sectionsHint = `${label(destinationFor("train").shortcut)}–${
    destinationFor("settings").shortcut.key
  } switches sections`;

  return (
    <div className={styles.sidebar} data-testid="sidebar">
      <div className={styles.wordmark}>
        <Text variant="caption2" tone="tertiary" uppercase as="div">
          {WORDMARK_EYEBROW}
        </Text>
        <Text variant="headline" as="div">
          {WORDMARK_TITLE}
        </Text>
      </div>

      <nav aria-label={SIDEBAR_LABEL} className={styles.groups}>
        {SIDEBAR_GROUPS.map((group) => (
          <div key={group.title} className={styles.group}>
            <Text variant="caption2" tone="tertiary" uppercase as="div">
              {group.title}
            </Text>
            {group.rows.map((row) => {
              const isSelected = isSidebarRowSelected(row, selected, buildTool);
              return (
                <button
                  key={row.id}
                  type="button"
                  data-testid={row.id}
                  className={isSelected ? `${styles.row} ${styles.rowSelected}` : styles.row}
                  aria-current={isSelected ? "page" : undefined}
                  onClick={() => {
                    onSelect(row.destination, row.tool);
                  }}
                >
                  <span className={styles.rowTitle}>{row.title}</span>
                  <span className={styles.rowSubtitle}>{row.subtitle}</span>
                </button>
              );
            })}

            {/* F-NAV-07 places Ringside inside the Start group, under Corner Board. */}
            {group.title === "Start" ? (
              <button
                type="button"
                data-testid="sidebar.ringside"
                className={styles.row}
                onClick={onRingside}
              >
                <span className={styles.rowTitle}>
                  {RINGSIDE_ACTION_TITLE}
                  <span
                    className={
                      quickAction.chip === "LIVE"
                        ? `${styles.chip} ${styles.chipLive}`
                        : styles.chip
                    }
                    aria-hidden="true"
                  >
                    {quickAction.chip}
                  </span>
                </span>
                <span className={styles.rowSubtitle}>{quickAction.subtitle}</span>
              </button>
            ) : null}
          </div>
        ))}

        {/* F-NAV-10: rendered only in Journal, and it disappears rather than
            disabling when the library cannot accept an entry. */}
        {selected === "journal" && newEntry !== null ? (
          <button
            type="button"
            data-testid="sidebar.newEntry"
            className={styles.row}
            onClick={newEntry}
          >
            <span className={styles.rowTitle}>{NEW_ENTRY_LABEL}</span>
            <span className={styles.rowSubtitle} aria-hidden="true">
              {label(NEW_ENTRY_SHORTCUT)}
            </span>
          </button>
        ) : null}
      </nav>

      {card !== null ? (
        <div
          className={styles.resume}
          data-testid="sidebar.resume"
          data-ff-tint={card.tint}
        >
          <Text variant="caption2" tone="tertiary" uppercase as="div">
            {card.state}
          </Text>
          <Text variant="callout" as="div">
            {card.title}
          </Text>
        </div>
      ) : null}

      <div className={styles.status} data-testid="sidebar.status">
        <VisuallyHidden>{`${status.privacy}. ${status.title}. ${status.state}.`}</VisuallyHidden>
        <span aria-hidden="true" className={styles.statusPrivacy}>
          {status.privacy}
        </span>
        <span aria-hidden="true" className={styles.statusTitle}>
          {status.title}
        </span>
        <span aria-hidden="true" className={styles.statusState}>
          {status.state}
        </span>
        {/* Visible, and hidden from AT: it teaches a keyboard affordance that a
            screen-reader user reaches another way, and reading it aloud after
            every status change is noise. */}
        <span aria-hidden="true" className={styles.statusHint} data-testid="sidebar.shortcutHint">
          {sectionsHint}
        </span>
      </div>
    </div>
  );
}
